<template>
    <page
        v-if="wizardData"
        :busy="busy"
        :tabs="tabsToRender"
        class="setup-property-page pb-1"
    >
        <router-view
            @busy="handleBusy"
            @setInvalid="setInvalid"
            @refetchInitialData="fetchInitialData"
        ></router-view>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../api.jsx';
import Page from '../../generic/page/page.vue';
import Modal from '../../generic/modal/modal.vue';
import { steps } from './constants';
import { RENTAL_PROPERTY_SETUP_WIZARD_ENDPOINT } from '../../../endpoints.js';
import { formatResponseError } from '../../../utils/api.js';

export default {
    name: 'SetupProperty',
    components: {
        Page,
        Modal,
    },
    provide() {
        return {
            propertyId: this.propertyId,
            getModuleEndpointAndBody: this.getModuleEndpointAndBody,
        };
    },
    computed: {
        ...mapGetters('setupPropertyStore', [
            'wizardData',
            'details',
            'address',
            'custom',
            'gallery',
        ]),
        propertyId() {
            return parseInt(this.$route.params.id);
        },
        localStorageActiveTabKey() {
            if (this.propertyId) {
                return 'setupPropertyActiveKey-' + this.propertyId;
            }
        },
        showPrevious() {
            return this.currentTabIndex > 0;
        },
        tabsToRender() {
            const tabs = this.wizardData && [
                ...this.getTabs(this.wizardData.system_type).map((item) => ({
                    ...item,
                })),
            ];
            return tabs;
        },
        tabs() {
            return [
                {
                    title: 'Details',
                    route: `/app/property/setup/details/${this.propertyId}/`,
                    module: 'details',
                    active: this.$route.path.includes(
                        `/app/property/setup/details/`
                    ),
                    callback: (value, index) =>
                        this.setActiveTab('details', index),
                },
                {
                    title: 'Address',
                    route: `/app/property/setup/address/${this.propertyId}/`,
                    active: this.$route.path.includes(
                        `/app/property/setup/address/`
                    ),
                    module: 'address',
                    callback: (value, index) =>
                        this.setActiveTab('address', index),
                },
                {
                    title: 'Rooms',
                    route: `/app/property/setup/rooms/${this.propertyId}/`,
                    active: this.$route.path.includes(
                        `/app/property/setup/rooms/`
                    ),
                    module: 'rooms',
                    callback: (value, index) =>
                        this.setActiveTab('rooms', index),
                },
                {
                    title: 'Amenities',
                    route: `/app/property/setup/amenities/${this.propertyId}/`,
                    active: this.$route.path.includes(
                        `/app/property/setup/amenities/`
                    ),
                    module: 'amenities',
                    callback: (value, index) =>
                        this.setActiveTab('amenities', index),
                },
                {
                    title: 'Rates / Rules',
                    route: `/app/property/setup/rates/${this.propertyId}/`,
                    active: this.$route.path.includes(
                        `/app/property/setup/rates/`
                    ),
                    module: 'rates',
                    callback: (value, index) =>
                        this.setActiveTab('rates', index),
                },
                {
                    title: 'Additional Info',
                    route: `/app/property/setup/additional/${this.propertyId}/`,
                    active: this.$route.path.includes(
                        `/app/property/setup/additional/`
                    ),
                    module: 'additional',
                    callback: (value, index) =>
                        this.setActiveTab('additional', index),
                },
                {
                    title: 'Gallery',
                    route: `/app/property/setup/gallery/${this.propertyId}/`,
                    active: this.$route.path.includes(
                        `/app/property/setup/gallery/`
                    ),
                    module: 'gallery',
                    callback: (value, index) =>
                        this.setActiveTab('gallery', index),
                },
                // {
                //     title: 'Reviews',
                //     route: `/app/property/setup/reviews/${this.propertyId}/`,
                //     active: this.$route.path.includes(
                //         `/app/property/setup/reviews/`
                //     ),
                //     module: 'reviews',
                //     callback: (value, index) =>
                //         this.setActiveTab('reviews', index),
                // },
                {
                    title: 'Custom',
                    route: `/app/property/setup/custom/${this.propertyId}/`,
                    active: this.$route.path.includes(
                        `/app/property/setup/custom/`
                    ),
                    module: 'custom',
                    callback: (value, index) =>
                        this.setActiveTab('custom', index),
                },
                {
                    title: 'Roomtypes',
                    route: `/app/property/setup/roomtypes/${this.propertyId}/`,
                    active: this.$route.path.includes(
                        `/app/property/setup/roomtypes/`
                    ),
                    module: 'roomtypes',
                    callback: (value, index) =>
                        this.setActiveTab('roomtypes', index),
                },
                {
                    title: 'Return',
                    module: 'parent',
                    callback: (value) =>
                        (window.location = `/app/property/setup/details/${this.wizardData?.inn}`),
                },
            ];
        },
        currentButtonLabel() {
            return this.tabsToRender &&
                this.currentTabIndex === this.tabsToRender.length - 1
                ? 'Finish'
                : 'Continue';
        },
    },
    data() {
        return {
            busy: false,
            steps,
            currentTabIndex: 0,
            isInvalid: false,
        };
    },
    mounted() {
        (async () => {
            this.handleBusy(true);
            await this.fetchWizardData();
            await this.fetchInitialData();
            this.currentTabIndex = this.getCurrentTabIndex();
            this.handleBusy(false);
        })();
    },
    methods: {
        setInvalid(value) {
            this.isInvalid = value;
        },
        getModuleInRoute(wizardData) {
            const currentTab = this.tabsToRender[this.currentTabIndex];
            const valid = this.steps[wizardData.system_type];
            return valid.some((item) => this.$route.path.indexOf(item) > 0);
        },
        getActiveTab(wizardData) {
            const currentTab = this.tabsToRender[this.currentTabIndex];
            const valid = this.steps[wizardData.system_type];
            const tab = localStorage.getItem(this.localStorageActiveTabKey);
            if (valid.includes(tab)) {
                return tab;
            }
            return null;
        },
        setActiveTab(value, index) {
            if (index !== undefined) {
                this.currentTabIndex = index;
            }
            localStorage.setItem(this.localStorageActiveTabKey, value);
        },
        detailsRedirect() {
            if (!this.$route.path.includes(`/app/property/setup/details/`)) {
                this.$router.push(
                    `/app/property/setup/details/${this.propertyId}/`
                );
            }
            this.setActiveTab('details', 0);
        },
        async fetchWizardData() {
            try {
                this.handleBusy(true);
                const { data } = await this.$store.dispatch(
                    'setupPropertyStore/getWizardData',
                    {
                        propertyId: this.propertyId,
                    }
                );
                if (!this.getModuleInRoute(data)) {
                    const tab = this.getActiveTab(data);
                    const isTheSameRoute = this.$route.path.includes(
                        `/app/property/setup/${tab}/`
                    );
                    if (tab) {
                        const index = this.tabsToRender.findIndex(
                            (item) => item.module === tab
                        );
                        this.currentTabIndex = index;
                    }
                    if (tab && !isTheSameRoute) {
                        this.$router.push(
                            `/app/property/setup/${tab}/${this.propertyId}`
                        );
                    } else if (!tab) {
                        this.detailsRedirect();
                    }
                }
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch wizard data' });
            } finally {
                this.handleBusy(false);
            }
        },
        async fetchInitialData() {
            try {
                this.handleBusy(true);
                await this.$store.dispatch(
                    'setupPropertyStore/getInitialData',
                    {
                        propertyId: this.propertyId,
                    }
                );
            } catch (e) {
                this.errorToast({ text: 'Could not fetch initial data' });
            } finally {
                this.handleBusy(false);
            }
        },
        handleBusy(busy) {
            this.busy = busy;
        },
        async cancelWizard() {
            try {
                this.handleBusy(true);
                await API.post(
                    `${RENTAL_PROPERTY_SETUP_WIZARD_ENDPOINT(
                        this.propertyId
                    )}cancel/`
                );
                this.wizardRedirect();
            } catch (e) {
                this.errorToast({ text: 'Could not cancel wizard' });
            } finally {
                this.handleBusy(false);
            }
        },
        wizardRedirect() {
            if (this.wizardData.inn != null) {
                window.location =
                    '/app/property/setup/roomtypes/' + this.wizardData.inn;
            } else if (this.wizardData.system_type == 'inn') {
                window.location =
                    '/app/property/setup/roomtypes/' + this.propertyId;
            } else {
                window.location = '/app/property/list/';
            }
        },
        getCurrentTabIndex() {
            const index = this.tabsToRender.findIndex(
                (item) => this.$route.path.indexOf(item.module) > 0
            );
            return index > 0 ? index : 0;
        },
        getTabs(systemType, isWizard) {
            const tabs = [];
            for (let i = 0; i < this.steps[systemType].length; i++) {
                for (let j = 0; j < this.tabs.length; j++) {
                    if (this.steps[systemType][i] == this.tabs[j].module) {
                        tabs.push(this.tabs[j]);
                    }
                }
            }
            return tabs;
        },
        getModuleEndpointAndBody() {
            const currentTab = this.tabsToRender[this.currentTabIndex];
            const module = {
                endpoint: `/app/property/setup/${currentTab.module}-data/${this.propertyId}/save/`,
                module: currentTab.module,
            };
            if (this[currentTab.module]) {
                module.body = JSON.parse(
                    JSON.stringify(this[currentTab.module])
                );
                if (currentTab.module === 'gallery') {
                    module.body = {
                        ...module.body.videos,
                    };
                }
                if (currentTab.module === 'details') {
                    module.body.additional_info =
                        module.body.additional_info || '';
                    module.body.airbnb_expectation =
                        module.body.airbnb_expectation
                            .filter((item) => item.selected)
                            .map((item) => ({
                                name: item.type,
                                description: item.description,
                            }));
                    module.body.check_out_tasks =
                        module.body.check_out_tasks
                            .filter((item) => item.selected)
                            .map((item) => ({
                                category: item.category,
                                description: item.description,
                            }));
                }
            }
            return module;
        },
    },
};
</script>

<style scoped lang="scss">
.setup-property-page {
    padding: 0 15px 60px 15px;
    :deep(ul.nav) {
        margin-left: 0 !important;
        margin-bottom: 0 !important;
        .nav-link.active {
            background-color: #f7f7f7 !important;
            margin-bottom: 0 !important;
            border-color: #dee2e6 #dee2e6 transparent;
        }
    }
    :deep(.lx-page-container) {
        padding: 0 0 30px 0;
        background-color: #f7f7f7;
        border-bottom: 1px solid #ddd;
        border-left: 1px solid #ddd;
        border-radius: 0 5px 5px 5px;
        border-right: 1px solid #ddd;
        @media (max-width: 768px) {
            padding: 0 0 120px 0;
        }
        .input-wrapper .vue-tel-input .form-phone-number-input-label {
            background-color: #fff !important;
        }
    }
    .form-tip {
        display: flex;
        align-items: center;
        height: 40px;
        margin-bottom: 1rem;
    }
    .proceed-setup-toolbar {
        width: 100%;

        position: fixed;
        bottom: 10px;
        left: 15px;
    }
}
</style>